import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  pillThemeToBadgeColor,
  ReturnStatus,
  returnStatusToBadgeColor,
  returnStatusToDisplayText,
} from "@redotech/redo-return-core-common/return-status";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DATE_FORMAT } from "@redotech/redo-web/time";
import { memo, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  compensationLabels,
  formatCustomerName,
  newItemsCount,
  orderedAt,
  paymentSummary,
  primaryOrderLabel,
  rejectionExplanation,
  ReturnDoc,
  totalItemCount,
} from "./parse-return-data";
import * as styles from "./return-summary-card.module.css";

/**
 * Three styled cards for the three known shapes of `get_return_data`
 * output: success, error, and disambiguation. Exported individually
 * so the tool-renderers registry can dispatch the right card based
 * on the parsed shape — the registry parses, this file just renders.
 */

export const ReturnErrorCard = memo(function ReturnErrorCard({
  error,
}: {
  error: string;
}) {
  return (
    <div className={styles.errorCard}>
      <Text fontSize="sm" textColor="primary">
        {error}
      </Text>
    </div>
  );
});

export const ReturnDocCard = memo(function ReturnDocCard({
  doc,
}: {
  doc: ReturnDoc;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  // Return detail nests under the all-returns list:
  //   /stores/<id>/returns/returns/all-returns/<returnId>
  const returnUrl = `${routes.getReturnsSubRoute("all-returns")}/${doc._id}`;

  const customerName = formatCustomerName(doc.customer);
  const customerEmail = doc.customer?.email?.trim() ?? null;
  const itemCount = totalItemCount(doc.products);
  const newItems = newItemsCount(doc);
  const rejection = rejectionExplanation(doc);
  const createdAt = parseIsoDate(doc.createdAt);
  const placedAt = orderedAt(doc.orders);
  const statusBadge = resolveStatusBadge(doc.status);
  const orderLabel = primaryOrderLabel(doc.orders);
  const typeLabels = compensationLabels(doc.returnType);
  const payment = paymentSummary(doc);
  const moneyFormatter = getCurrencyFormatter(doc.currency);
  const canProcess = isProcessableStatus(doc.status);

  const headline = orderLabel
    ? `Order ${orderLabel}`
    : `${kindFor(doc.type)} · ${shortId(doc._id)}`;

  return (
    <div className={styles.card}>
      <Flex dir="column" gap="xs">
        <Flex align="center" gap="sm" wrap="wrap">
          <Text fontSize="md" fontWeight="semibold">
            {headline}
          </Text>
          {statusBadge && (
            <RedoBadge
              color={statusBadge.color}
              size="xs"
              text={statusBadge.label}
            />
          )}
          {typeLabels.map((label) => (
            <RedoBadge color="gray" key={label} size="xs" text={label} />
          ))}
        </Flex>
        {(customerName || customerEmail) && (
          <Text fontSize="xs" textColor="secondary">
            {[customerName, customerEmail].filter(Boolean).join(" · ")}
          </Text>
        )}
      </Flex>

      <Flex dir="column">
        <Row
          label="Return items"
          value={<RedoBadge color="gray" size="xs" text={String(itemCount)} />}
        />
        {newItems > 0 && (
          <Row
            label="New items"
            value={<RedoBadge color="gray" size="xs" text={String(newItems)} />}
          />
        )}
        {payment && (
          <Row
            label={payment.kind === "owed" ? "Payment owed" : "Refund amount"}
            value={
              <Text fontSize="xs" fontWeight="semibold">
                {moneyFormatter.format(payment.amount)}
              </Text>
            }
          />
        )}
        {placedAt && (
          <Row
            label="Ordered"
            value={
              <Text fontSize="xs" textColor="primary">
                {DATE_FORMAT.format(placedAt)}
              </Text>
            }
          />
        )}
        {createdAt && (
          <Row
            isLast
            label="Return created"
            value={
              <Text fontSize="xs" textColor="primary">
                {DATE_FORMAT.format(createdAt)}
              </Text>
            }
          />
        )}
      </Flex>

      {rejection && (
        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" fontWeight="semibold" textColor="primary">
            Rejection note
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {rejection}
          </Text>
        </Flex>
      )}

      <Flex dir="column" gap="sm">
        {canProcess && (
          <RedoButton
            className={styles.actionButton}
            hierarchy="primary"
            onClick={() => navigate(returnUrl)}
            size="sm"
            text="Process"
          />
        )}
        <RedoButton
          className={styles.actionButton}
          hierarchy="secondary"
          onClick={() => navigate(returnUrl)}
          size="sm"
          text="View return"
        />
      </Flex>
    </div>
  );
});

export const DisambiguationCard = memo(function DisambiguationCard({
  data,
}: {
  data: { orderId: string; returns: readonly string[] };
}) {
  return (
    <div className={styles.card}>
      <Text fontSize="sm" fontWeight="semibold">
        Multiple returns for order {data.orderId}
      </Text>
      <Text fontSize="xs" textColor="secondary">
        Tell Ask Redo which one you mean.
      </Text>
      <ul className={styles.disambiguationList}>
        {data.returns.map((line, idx) => (
          <li key={idx}>
            <Text fontSize="xs" textColor="primary">
              {line}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
});

const Row = memo(function Row({
  label,
  value,
  isLast,
}: {
  label: string;
  value: ReactNode;
  isLast?: boolean;
}) {
  return (
    <>
      <Flex
        align="center"
        className={styles.row}
        justify="space-between"
        py="sm"
        w="full"
      >
        <Text fontSize="xs" textColor="primary">
          {label}
        </Text>
        {value}
      </Flex>
      {!isLast && <Divider />}
    </>
  );
});

/**
 * Look up the canonical badge color + label for a return status. Falls
 * back to a neutral grey + the raw value for statuses we don't know
 * about, so the card doesn't crash on a future server-side enum.
 */
function resolveStatusBadge(
  status: string | null | undefined,
): { color: ReturnType<typeof pillThemeToBadgeColor>; label: string } | null {
  if (!status) {
    return null;
  }
  const theme = returnStatusToBadgeColor[status as ReturnStatus];
  if (!theme) {
    return { color: "gray", label: status };
  }
  return {
    color: pillThemeToBadgeColor(theme),
    label: returnStatusToDisplayText[status as ReturnStatus],
  };
}

/**
 * "Processable" returns are ones where the merchant still has work to
 * do (review, approve, refund). Complete / rejected / deleted returns
 * are terminal — surface only the View action.
 */
function isProcessableStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }
  switch (status) {
    case ReturnStatus.COMPLETE:
    case ReturnStatus.REJECTED:
    case ReturnStatus.DELETED:
      return false;
    default:
      return true;
  }
}

function kindFor(type: string | null | undefined): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (type) {
    case "warranty":
      return "Warranty";
    case "claim":
      return "Claim";
    default:
      return "Return";
  }
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
